import { FC, useRef, useState, useEffect, useContext, Dispatch, SetStateAction } from 'react'
import dayjs from 'dayjs'
import { Box, Button, Select, MenuItem, InputLabel, Typography, FormControl, useTheme } from '@mui/material'
import { createExerciseLocation, createRequest } from '../../../../services/request-form.service'
import { IRequest } from '../../../../types/exercise.type'
import { RequestContext } from '../../../../contexts/request.context'
import { RequestEquipmentContext } from '../../../../contexts/request-equipment.context'
import { useToast } from '../../../../contexts/toast.context'
import { AuthContext } from '../../../../contexts/auth.context'

import styles from './styles.module.scss'

interface IProps {
  isLightMode: boolean
  setValue: Dispatch<SetStateAction<number>>
}

type ExerciseTypes = 'my requests' | 'all unit requests'

const Sidebar: FC<IProps> = ({ setValue, isLightMode }) => {
  const theme = useTheme()

  const [exercisesType, setExercisesType] = useState<ExerciseTypes>('my requests')
  const [selectedExercise, setSelectedExercise] = useState(0)

  const { showToast } = useToast()

  const {
    exercises,
    exerciseRequests,
    selectedRequest,
    setSelectedFrequencyGroup,
    setSelectedRequestLocation,
    getRequests,
    setRequests,
    setSelectedRequest,
  } = useContext(RequestContext)

  const { user } = useContext(AuthContext)
  const { setEquipment } = useContext(RequestEquipmentContext)

  const sidebarRef = useRef<HTMLDivElement>(null)

  const handleCreate = async () => {
    const data = await createRequest(selectedExercise)

    if (data.success && data.data) {
      const parsedRequest = {
        ...data.data,
        start_date: dayjs(data.data.start_date),
        end_date: dayjs(data.data.end_date),
      }
      setRequests([parsedRequest, ...exerciseRequests])
      setSelectedRequest(parsedRequest)
      setValue(0)

      showToast('success', 'Request created successfully')

      await createExerciseLocation(data.data.id)
    }
  }

  const handleRequestChange = (request: IRequest) => {
    setSelectedRequest(request)
    setEquipment(null)
    setSelectedFrequencyGroup(null)
    setSelectedRequestLocation(null)
  }

  useEffect(() => {
    getRequests(exercisesType, selectedExercise || null)
  }, [exercisesType, selectedExercise, user])

  return (
    <Box
      className={`${styles.sidebar} sub-container`}
      ref={sidebarRef}
      sx={{
        background: theme.palette.elevated.main,
        borderRight: '1px solid  ' + theme.palette.elevated.light,
      }}
    >
      <Box className={styles.form}>
        <FormControl sx={{ width: '100%' }}>
          <Select
            className={styles.select}
            value={exercisesType}
            onChange={(evt) => setExercisesType(evt.target.value as ExerciseTypes)}
            size="small"
          >
            <MenuItem value="my requests">My Requests</MenuItem>
            <MenuItem value="all unit requests">All Unit Requests</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ width: '100%' }}>
          <InputLabel id="exercise">Exercise</InputLabel>
          <Select
            className={styles.select}
            labelId="exercise"
            value={selectedExercise}
            label="Exercise"
            onChange={(evt) => setSelectedExercise(Number(evt.target.value))}
            size="small"
          >
            {[...exercises, { name: 'None', id: 0 }].map((exercise) => (
              <MenuItem key={exercise.id} value={exercise.id}>
                {exercise.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button onClick={handleCreate} className={styles.createBtn} variant="contained">
          Create
        </Button>
      </Box>
      <Typography className={styles.requestsTitle}>Requests:</Typography>
      {!!exerciseRequests.length && (
        <Box className={styles.exerciseRequests}>
          {exerciseRequests.map((elem) => (
            <Box
              key={elem.id}
              className={styles.exerciseRequest}
              sx={
                elem.id === selectedRequest?.id
                  ? {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.elevated.contrastText,
                      background: theme.palette.elevated.light,
                    }
                  : {
                      color: theme.palette.elevated.contrastText,
                      borderColor: theme.palette.elevated.dark,
                      background: 'none',
                    }
              }
              onClick={() => handleRequestChange(elem)}
            >
              <Typography>
                {elem.name ? elem.name : `${elem.username}: ${new Date(elem.created_at).toTimeString()}`}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default Sidebar
